import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { getErrorMessage } from '@/lib/utils'
import {
  bulkDeleteUserAddresses,
  createUserAddress,
  deleteUserAddress,
  fetchUserAddress,
  fetchUserAddresses,
  setPrimaryUserAddress,
  updateUserAddress
} from '@/lib/api'
import {
  AddressType,
  AddressesFilterParams,
  AddressesListResponse,
  CreateAddressParams,
  UpdateAddressParams
} from '@/types/address'
import { BulkDeleteResponse } from '@/types/general'

type AddressStateType = {
  isLoading: boolean
  response: AddressType | null
  error: {
    status?: number
    message: string
  } | null
}

type AddressesListStateType = {
  isLoading: boolean
  response: AddressesListResponse | null
  error: {
    status?: number
    message: string
  } | null
}

type AddressesStoreState = {
  addressesListState: AddressesListStateType
  fetchAddresses: (userId: string, params?: AddressesFilterParams) => Promise<AddressesListResponse>
  resetAddressesList: () => void
  addressState: AddressStateType
  fetchAddress: (userId: string, addressId: string) => Promise<AddressType>
  createAddress: (userId: string, params: CreateAddressParams) => Promise<AddressType>
  updateAddress: (userId: string, addressId: string, params: UpdateAddressParams) => Promise<AddressType>
  deleteAddress: (userId: string, addressId: string) => Promise<void>
  bulkDeleteAddresses: (userId: string, ids: string[]) => Promise<BulkDeleteResponse>
  setPrimaryAddress: (userId: string, addressId: string) => Promise<AddressType>
  resetAddress: () => void
}

const initialAddressState: AddressStateType = {
  isLoading: false,
  response: null,
  error: null
}

const initialAddressesListState: AddressesListStateType = {
  isLoading: false,
  response: null,
  error: null
}

const useAddressesStore = create<AddressesStoreState>()(
  devtools((set, get) => ({
    addressesListState: initialAddressesListState,
    addressState: initialAddressState,

    fetchAddresses: async (userId: string, params: AddressesFilterParams = {}) => {
      const currentState = get().addressesListState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({
        addressesListState: {
          ...initialAddressesListState,
          isLoading: true
        }
      }))

      try {
        const data = await fetchUserAddresses(userId, params)

        set(() => ({
          addressesListState: {
            ...initialAddressesListState,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          addressesListState: {
            ...initialAddressesListState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    resetAddressesList: () => {
      set(() => ({
        addressesListState: initialAddressesListState
      }))
    },

    fetchAddress: async (userId: string, addressId: string) => {
      const currentState = get().addressState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({
        addressState: {
          ...initialAddressState,
          isLoading: true
        }
      }))

      try {
        const data = await fetchUserAddress(userId, addressId)

        set(() => ({
          addressState: {
            ...initialAddressState,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          addressState: {
            ...initialAddressState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    createAddress: async (userId: string, params: CreateAddressParams) => {
      set(() => ({
        addressState: {
          ...initialAddressState,
          isLoading: true
        }
      }))

      try {
        const data = await createUserAddress(userId, params)

        set(() => ({
          addressState: {
            ...initialAddressState,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          addressState: {
            ...initialAddressState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    updateAddress: async (userId: string, addressId: string, params: UpdateAddressParams) => {
      set(() => ({
        addressState: {
          ...get().addressState,
          isLoading: true
        }
      }))

      try {
        const data = await updateUserAddress(userId, addressId, params)

        set(() => ({
          addressState: {
            ...get().addressState,
            isLoading: false,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          addressState: {
            ...initialAddressState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    deleteAddress: async (userId: string, addressId: string) => {
      set(() => ({
        addressState: {
          ...get().addressState,
          isLoading: true
        }
      }))

      try {
        await deleteUserAddress(userId, addressId)

        set(() => ({
          addressState: initialAddressState
        }))
      } catch (error: any) {
        set(() => ({
          addressState: {
            ...initialAddressState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    bulkDeleteAddresses: async (userId: string, ids: string[]) => {
      set(state => ({
        addressState: {
          ...state.addressState,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeleteUserAddresses(userId, { ids })

        set(state => ({
          addressState: {
            ...state.addressState,
            isLoading: false
          }
        }))

        return response
      } catch (error: any) {
        set(state => ({
          addressState: {
            ...state.addressState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            },
            isLoading: false
          }
        }))

        throw error
      }
    },

    setPrimaryAddress: async (userId: string, addressId: string) => {
      set(() => ({
        addressState: {
          ...get().addressState,
          isLoading: true
        }
      }))

      try {
        const data = await setPrimaryUserAddress(userId, addressId)

        set(() => ({
          addressState: {
            ...get().addressState,
            isLoading: false,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          addressState: {
            ...initialAddressState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    resetAddress: () => {
      set(() => ({
        addressState: initialAddressState
      }))
    }
  }))
)

export default useAddressesStore
